/**
 * The persistent shell: top bar, navigation rail, content region.
 *
 * The rail is the reference's drawing index reduced to what an application
 * needs: one entry per organization-scoped section V1 section 9 names, and one
 * real number. That is six, and it is counted rather than asserted —
 * `test/nav-screen-coverage.test.ts` parses the "Required screens" table out of
 * `docs/V1.md` and fails if the rail and the table disagree in either
 * direction.
 *
 * Toolkits was the one missing. V1's screen table (`:359`) and its own layout
 * wireframe (`:371`) both name it, and the route was served and rendered
 * (`lib/router.ts:106`, `app.ts:698`), but nothing linked to it, so the only
 * way onto that screen was to type its URL.
 *
 * One table row names two sections, which is why the count above is of
 * SECTIONS and not of rows: the Skills rows are `/o/:org/skills`,
 * `/o/:org/skills/:id` and `/o/:org/skills/:id/edit`, and the Reviews row is
 * `/o/:org/reviews/:draftId` — a generated id, falling back to the library when
 * no drafts are pending.
 *
 * There is no metrics row above the content, because V1 section 9 forbids
 * reproducing speculative metric cards and the only counts here are counts of
 * things a person can click through to.
 */

import { type RawHtml, cls, html, raw } from '../lib/html.js';
import type { AppShell } from '../app.js';
import { renderDialog } from './dialog.js';
import { hrefFor } from '../lib/router.js';

interface Destination {
  readonly label: string;
  readonly href: string;
  readonly current: boolean;
  readonly count: number | null;
}

export function renderShell(app: AppShell, inner: RawHtml): RawHtml {
  const organization = app.state.organization;
  const slug = organization?.slug ?? '';
  const pending = app.state.reviewQueue.length;

  const destinations: readonly Destination[] = [
    { label: 'Skills', href: `/o/${slug}/skills`, current: isCurrent(app, 'skills'), count: null },
    {
      label: 'Toolkits',
      href: `/o/${slug}/toolkits`,
      current: isCurrent(app, 'toolkits'),
      count: null,
    },
    {
      label: 'Improvements',
      href: `/o/${slug}/improvements`,
      current: isCurrent(app, 'improvements'),
      count: null,
    },
    { label: 'Reviews', href: pending > 0 ? `/o/${slug}/reviews/${app.state.reviewQueue[0]?.id ?? ''}` : `/o/${slug}/skills`, current: isCurrent(app, 'review'), count: pending > 0 ? pending : null },
    { label: 'Connect', href: `/o/${slug}/connect`, current: isCurrent(app, 'connect'), count: null },
    { label: 'Admin', href: `/o/${slug}/admin`, current: isCurrent(app, 'admin'), count: null },
  ];

  return html`
    <div class="shell">
      <header class="topbar">
        <a class="wordmark" href="/login">Skills Manager</a>
        ${organizationSelector(app)}
        ${buttonTheme(app)}
        ${buttonSignOut(app)}
      </header>
      <div class="body-row">
        <nav class="rail" aria-label="Sections">
          <div class="rail-group">${slug}</div>
          ${destinations.map(
            (item) => html`
              <a
                class="nav-item"
                href="${item.href}"
                ${item.current ? 'aria-current="page"' : ''}
              >
                <span>${item.label}</span>
                ${item.count === null ? '' : html`<span class="nav-count">${item.count}</span>`}
              </a>
            `,
          )}
        </nav>
        <main class="content" data-scroll="content">
          <div class="content-inner">
            ${offlineBanner(app)} ${noticeBar(app)} ${inner}
          </div>
        </main>
      </div>
    </div>
    ${renderDialog(app)}
  `;
}

/**
 * The organization selector DESIGN.md:174-175 specifies, and the honest
 * version of it.
 *
 * WHAT IT LISTS, AND WHY THAT IS ONE ENTRY
 *   The organizations this session can act in, which is exactly what the
 *   verified token names. `memberships_read` is pinned to `authz.current_org()`
 *   (0025_current_org_pinning.sql:165-170), so a query for "every organization
 *   I belong to" is answered by RLS with the selected organization alone. A
 *   selector listing anything else would be listing memberships the database
 *   refuses to disclose — a list this deployment cannot honestly produce.
 *
 *   So the control lists what is actually selectable, which is the bound
 *   organization, and it is a real `<select>` with a real handler rather than a
 *   static label wearing a selector's name. Choosing it navigates, and the slug
 *   it navigates to is re-checked against the token by `App.load`
 *   (app.ts:309-312), which answers a slug the token does not name with
 *   not-found. Multi-organization switching becomes real the moment the token
 *   and the pinning policy support it, and it will not require editing this
 *   component: the option list is already built from the selectable set.
 *
 * WHY THE VALUE IS NOT AUTHORITY
 *   The slug here reaches `App` from a control, and consent binds to
 *   `identity.orgId` on the API side regardless (mcp/oauth/consent.ts:219).
 *   Picking an organization here chooses a route. It cannot widen what the
 *   token grants, and it is not a place an organization can be asserted.
 */
function organizationSelector(app: AppShell): RawHtml {
  const organization = app.state.organization;
  if (organization === null) {
    return html`<span class="org-label">No organization selected</span>`;
  }
  const destination =
    'org' in app.state.route ? hrefFor({ ...app.state.route, org: organization.slug }) : null;
  return html`
    <label class="org-picker">
      <span class="visually-hidden">Organization</span>
      <select class="field org-picker-field" data-field="org.select">
        <option value="${organization.slug}" selected>${organization.name}</option>
      </select>
    </label>
    ${destination === null
      ? ''
      : html`<a class="visually-hidden" href="${destination}">Go to ${organization.name}</a>`}
  `;
}

function isCurrent(app: AppShell, name: string): boolean {
  const route = app.state.route;
  if (name === 'review') return route.name === 'review';
  if (name === 'skills') return route.name === 'skills' || route.name === 'skill' || route.name === 'skill-edit';
  return route.name === name;
}

function buttonTheme(app: AppShell): RawHtml {
  const next = app.state.theme === 'dark' ? 'light' : 'dark';
  return html`<button class="btn btn-quiet" data-action="theme:toggle">
    ${app.state.theme === null ? 'Follow system theme' : `Theme: ${app.state.theme}`}
    <span class="visually-hidden">, switch to ${next}</span>
  </button>`;
}

function buttonSignOut(app: AppShell): RawHtml {
  return html`<button class="btn btn-secondary" data-action="sign-out">Sign out</button>`;
}

function offlineBanner(app: AppShell): RawHtml {
  if (!app.state.offline) return raw('');
  return html`
    <div class="banner" role="status">
      <span>This browser reports no network connection. Requests will fail until it is back.</span>
    </div>
  `;
}

function noticeBar(app: AppShell): RawHtml {
  const notice = app.state.notice;
  if (notice === null) return raw('');
  return html`
    <div
      class="${cls('callout', notice.tone === 'bad' ? 'callout-danger' : 'callout-attention')}"
      role="status"
      aria-live="polite"
    >
      <p>${notice.text}</p>
      <div class="btn-row">
        <button class="btn btn-secondary" data-action="notice:clear">Dismiss</button>
      </div>
    </div>
  `;
}
